import AddIcon from '@mui/icons-material/Add';
import { ButtonBase, Divider, Stack, Typography, alpha } from '@mui/material';
import { deleteProjectWrapper, getAllProjectWrappers } from 'API/project-wrapper';
import { Routes, ZoomLevels } from 'enums';
import PaiPagination from 'features/ui/base-components/pai-pagination';
import { useUserData } from 'hooks/use-user-data';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import useAppBarNavigationStore, { NavigationStates } from 'stores/app-bar-navigation-store';
import useErrorStore from 'stores/error-store';
import { Shadows } from 'theme';
import { makeStyles } from 'tss-react/mui';
import PaiDotLoader from '../../ui/base-components/pai-dot-loader';
import PaiTextField from '../../ui/base-components/pai-text-field';
import DeleteLinkedinCrawlerProjectDialog from './delete-linkedin-crawler-project-dialog';
import ProjectWrapperItem from './linkedin-crawler-project-item';

interface Props {
  title: string;
  type: 'normal' | 'li_search';

  resetAll(): void;
}

export interface ProjectWrapper {
  _id: string;
  name: string;
  createDate: string;
}

const LinkedinCrawlerOverview: React.FC<Props> = ({ title, type, resetAll }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  useUserData();

  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);
  const { setAppBarNavigation } = useAppBarNavigationStore((state) => ({ setAppBarNavigation: state.setActiveTab }));
  const navigate = useNavigate();
  const location = useLocation();

  const [wrappers, setWrappers] = useState<ProjectWrapper[]>([]);
  const [fetchWrappersLoading, setFetchWrappersLoading] = useState(false);
  const [searchParam, setSearchParam] = useState('');

  const [deleteProjectWrapperDialog, setDeleteProjectWrapperDialog] = useState<{ open: boolean; wrapper?: ProjectWrapper }>({
    open: false,
  });

  const itemsPerPage = 25;
  const [itemCount, setItemCount] = useState(0);
  const [page, setPage] = useState<number>(1);

  let typingTimer: ReturnType<typeof setTimeout>;

  const paiLogo = '/dashboard/images/logos/product_logo_white.svg';

  const fetchWrappers = async (newPage: number = page, newSearchParam: string = searchParam) => {
    setFetchWrappersLoading(true);

    const res = await getAllProjectWrappers(type, newPage - 1, itemsPerPage, newSearchParam);

    if (res && !res.error) {
      if (_.isEmpty(res.projectWrappers) && newSearchParam !== '') {
        setWrappers([]);
        setItemCount(0);
        setPage(1);
      } else {
        setWrappers(res.projectWrappers);
        setItemCount(res.total);
        setPage(newPage);
      }
    } else {
      setWrappers([]);
      setItemCount(0);
      setPage(1);
      handleOpenErrorDialog(res?.error, '/projects/wrappers', 'get');
    }

    setFetchWrappersLoading(false);
  };

  useEffect(() => {
    if (!_.isNil(type)) {
      fetchWrappers();
    }
  }, [type, itemsPerPage]);

  /**
   * Search handlers
   */

  const handleSearchParamChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchParam(event.target.value);
  };

  const handleSearchWrappers = async () => {
    fetchWrappers(1, searchParam);
  };

  const searchKeyUp = () => {
    clearTimeout(typingTimer);
    typingTimer = setTimeout(() => {
      handleSearchWrappers();
    }, 500);
  };

  const searchKeyDown = () => {
    clearTimeout(typingTimer);
  };

  /**
   * Pagination handlers
   */

  const handleChangePage = (event: unknown, newPage: number) => {
    fetchWrappers(newPage);
  };

  /**
   * Delete booster project dialog handlers
   */

  const handleOpenConfirmDeleteDialog = (wrapper: ProjectWrapper) => {
    return () => {
      setDeleteProjectWrapperDialog({ open: true, wrapper });
    };
  };

  const handleCloseConfirmDeleteDialog = () => {
    setDeleteProjectWrapperDialog({ open: false, wrapper: undefined });
  };

  const deleteWrapper = async () => {
    const { wrapper } = deleteProjectWrapperDialog;

    if (_.isNil(wrapper)) return;

    const res = await deleteProjectWrapper(wrapper._id);

    if (res && !res.error) {
      const newPage = wrappers?.length === 1 ? page - 1 : page;
      fetchWrappers(newPage);
      handleCloseConfirmDeleteDialog();
    } else {
      handleOpenErrorDialog(res?.error, '/projects/wrappers/:wrapperId', 'delete');
    }
  };

  const handleNewProjectClick = () => {
    if (type === 'normal') {
      navigate(Routes.PERSONA_GENERATOR);
      setAppBarNavigation(NavigationStates.TOOL);
    }

    if (type === 'li_search') {
      navigate(Routes.URL_FINDER, { state: location.state });
      setAppBarNavigation(NavigationStates.TOOL);
    }

    resetAll();
  };

  return (
    <>
      <div className={classes.container}>
        <Stack className={classes.content} gap={1} justifyContent={'flex-start'}>
          <div className={classes.title}>
            <Typography className={classes.titleText}>{title}</Typography>
          </div>
          <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
            <Stack direction={'row'} alignItems={'center'} gap={2}>
              <img className={classes.productGroupLogo} src={paiLogo} alt="booster logo" />
              <Divider className={classes.divider} orientation={'vertical'} />
              <PaiTextField
                value={searchParam}
                onChange={handleSearchParamChange}
                onKeyDown={searchKeyDown}
                onKeyUp={searchKeyUp}
                placeholder={t('enterProjectName')}
                disableBorder={true}
                style={{ width: '350px' }}
              />
              <Divider className={classes.divider} orientation={'vertical'} />
              <ButtonBase className={classes.actionButton} onClick={handleNewProjectClick}>
                <Stack direction={'row'} alignItems={'center'} gap={1}>
                  <AddIcon className={classes.icon} />
                  {t('newProject')}
                </Stack>
              </ButtonBase>
            </Stack>
            <PaiPagination
              total={itemCount}
              itemsPerPage={itemsPerPage}
              page={page}
              hidden={wrappers?.length === 0 || itemCount <= itemsPerPage}
              handleChangePage={handleChangePage}
            />
          </Stack>
          {fetchWrappersLoading ? (
            <PaiDotLoader />
          ) : !_.isEmpty(wrappers) ? (
            <Stack className={classes.projects} direction={'column'}>
              {wrappers.map((wrapper, index) => (
                <ProjectWrapperItem
                  key={wrapper._id}
                  isEven={index % 2 === 0}
                  wrapper={wrapper}
                  type={type}
                  handleOpenConfirmDeleteDialog={handleOpenConfirmDeleteDialog}
                />
              ))}
            </Stack>
          ) : (
            <div className={classes.noProjectsRow}>
              <Typography className={classes.noProjects} align={'center'}>
                {t('noProjectsFound')}
              </Typography>
            </div>
          )}
        </Stack>
      </div>

      <DeleteLinkedinCrawlerProjectDialog
        open={deleteProjectWrapperDialog.open}
        wrapper={deleteProjectWrapperDialog.wrapper}
        handleClose={handleCloseConfirmDeleteDialog}
        deleteWrapper={deleteWrapper}
      />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    background: theme.palette.backgrounds.main,
    height: 'calc(100vh - 60px - 32px)',
    padding: '8px',

    [theme.breakpoints.down('xl')]: {
      height: `calc((100vh / ${ZoomLevels.XL}) - 60px - 32px)`,
    },
    [theme.breakpoints.down(1300)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_LG}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('lg')]: {
      height: `calc((100vh / ${ZoomLevels.LG}) - 60px - 32px)`,
    },
    [theme.breakpoints.down(1000)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_MD}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('md')]: {
      height: `calc((100vh / ${ZoomLevels.MD}) - 60px - 32px)`,
    },
  },
  title: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 'bold',
    textAlign: 'center',
    textTransform: 'uppercase',
  },
  productGroupLogo: {
    height: '32px',
    width: 'auto',
  },
  divider: {
    height: '24px',
    borderRightWidth: '2px',
  },
  actionButton: {
    fontSize: theme.typography.pxToRem(13),
    borderRadius: '2px',
    padding: '10px 8px 10px 0',
    color: theme.palette.greys.medium,
    whiteSpace: 'nowrap',
    '&:hover': {
      background: alpha('#000', 0.05),
      color: theme.palette.primary.main,
    },
  },
  icon: {
    fontSize: theme.typography.pxToRem(20),
  },
  content: {
    background: '#FFF',
    boxShadow: Shadows.GREY_SHADOW_LIGHT,
    padding: '12px 16px',
    height: '100%',
  },
  projects: {
    maxHeight: 'calc(100% - 19.5px - 40px - 16px)',
    border: `2px solid ${theme.palette.backgrounds.main}`,
    width: '100%',
    overflow: 'auto',
  },
  noProjectsRow: {
    width: '100%',
    padding: '8px',
    minHeight: '36px',
    border: `2px solid ${theme.palette.backgrounds.main}`,
  },
  noProjects: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
    padding: '0 4px',
  },
}));

export default LinkedinCrawlerOverview;
